import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { PostTypesDataService, PostTypesColumnsService } from '../../services';
import { DialogPostTypeFormComponent } from './post-type-form/post-type-form.component';
import { DialogPostTypeDetailsComponent } from './post-type-details/post-type-details.component';
import { PostType, PostTypesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-post-types',
  templateUrl: './post-types.component.html',
  styleUrls: ['./post-types.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class PostTypesComponent implements OnInit, OnDestroy {

  private postTypeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private postTypeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private postTypeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public postTypeToDelete: PostType = {};
  public postTypes: PostType[] = [];
  public postType: PostType = {};
  public selectedPostTypes: PostType[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private postTypesDataService: PostTypesDataService,
    private postTypesColumnsService: PostTypesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('PostTypesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Post Types - Management');
    this.primengConfig.ripple = true;
    this.getPostTypes();
  }

  ngOnDestroy(): void {
    console.log('PostTypesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getPostTypes(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.postTypesDataService.getPostTypes(this.parameters).subscribe((response: PostTypesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.postTypes = response.results;
        } else {
          this.postTypes = this.postTypes.concat(response.results);
        }
        if (response.inlineCount > this.postTypes.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getPostTypes();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTTYPESNOTLOADED') });
    });
  }

  importPostTypes(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.postTypesDataService.addPostTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('POSTTYPESCREATED') });
                if (proceed === j) {
                  this.getPostTypes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTTYPESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTTYPESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.postTypesDataService.updatePostTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('POSTTYPESUPDATED') });
                if (proceed === j) {
                  this.getPostTypes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTTYPESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTTYPESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.postTypesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('postTypes.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedPostTypes() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Post Types?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.postTypesDataService.deletePostTypes(this.selectedPostTypes).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Post Types Deleted', life: 3000 });
            this.getPostTypes();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Post Types not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Post Types not deleted' });
        });
        this.selectedPostTypes = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.postTypes, this.columns, 'PostTypes');
  }

  exportExcel() {
    ToolsService.exportExcel(this.postTypes, this.columns, 'PostTypes');
  }

  exportCsv() {
    ToolsService.exportCsv(this.postTypes, this.columns, 'PostTypes');
  }

  refresh() {
    this.postTypesDataService.clearCache();
    this.getPostTypes();
  }

  postTypeAdd(): void {
    if (!this.navigation.getParamValue('postTypes.addInDialog', true)) {
      this.router.navigate(['/post-types/add']);
      return;
    }
    this.postTypeAddDialog = this.dialogService.open(DialogPostTypeFormComponent, {
      header: '', // this.translate.instant('NEWPOSTTYPE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        postType: { id: null },
        users: this.postTypesColumnsService.users,
      },
    });

    this.postTypeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'PostType added' });
        this.getPostTypes();
      }
    });
  }

  postTypeEdit(postType: PostType): void {
    if (!this.navigation.getParamValue('postTypes.editInDialog', true)) {
      this.router.navigate(['/post-types/edit/' + postType.id]);
      return;
    }
    this.postTypeEditDialog = this.dialogService.open(DialogPostTypeFormComponent, {
      header: '', // this.translate.instant('EDITPOSTTYPE') + ' ' + postType.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        postType: postType,
        users: this.postTypesColumnsService.users,
      },
    });

    this.postTypeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'PostType updated' });
        this.getPostTypes();
      }
    });
  }

  postTypeDetails(postType: PostType): void {
    if (!this.navigation.getParamValue('postTypes.detailsInDialog', true)) {
      this.router.navigate(['/post-types/details/' + postType.id]);
      return;
    }
    this.postTypeDetailsDialog = this.dialogService.open(DialogPostTypeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        postType: postType,
      }
    });
  }

  postTypeDelete(postType: PostType): void {
    this.postTypeToDelete = postType;
    this.messageService.clear();
    this.messageService.add({
      key: 'postTypeDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + postType.name + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.postTypesDataService.deletePostType(this.postTypeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Post Type deleted' });
      this.getPostTypes();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Post Type not deleted' });
    });
  }

  onDeleteReject(): void {
    this.postTypeToDelete = {};
    this.messageService.clear('postTypeDelete');
  }

}
